import { useEffect, useState, useMemo } from 'react';
import { api } from '../../api/api';

interface Service {
  id: number;
  title: string;
  description: string;
}

export default function ServicesList() {
  const [services, setServices] = useState<Service[]>([]);
  // Стан для керування відкритими секціями (null - все закрито, або 'psychiatry' | 'psychology')
  const [activeCategory, setActiveCategory] = useState<string | null>('psychiatry');

  useEffect(() => {
    let isMounted = true;
    api.getServices().then(data => {
      if (isMounted) setServices(data || []);
    });
    return () => { isMounted = false; };
  }, []);

  const groupedServices = useMemo(() => {
    const psychiatry = services
      .filter(s => s.title.trim().startsWith('1'))
      .map(s => ({ ...s, title: s.title.replace(/^1[\s.]*/, '').trim() }));
    
    const psychology = services
      .filter(s => s.title.trim().startsWith('2'))
      .map(s => ({ ...s, title: s.title.replace(/^2[\s.]*/, '').trim() }));

    return { psychiatry, psychology };
  }, [services]);

  const toggleCategory = (category: string) => {
    setActiveCategory(activeCategory === category ? null : category);
  };

  const renderSimpleList = (list: Service[], isOpen: boolean) => (
    <div className={`modern-service-list ${isOpen ? 'is-open' : 'is-closed'}`}>
      {list.map((s, index) => (
        <div key={s.id || index} className="list-item-simple">
          <div className="list-item-number">
            {index + 1 < 10 ? `0${index + 1}` : index + 1}
          </div>
          <div className="list-item-content">
            <h4 className="list-item-title">{s.title}</h4>
            {/* Опис виводиться лише якщо він не порожній */}
            {s.description && <p className="list-item-description">{s.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="services-page-container">
      <div className="services-content">
        
        <header className="services-header">
          <h2 className="services-main-title">Напрямки допомоги</h2>
          <div className="services-divider"></div>
          <p className="services-subtitle">
            Ми пропонуємо комплексний підхід до вашого здоров'я, поєднуючи сучасні технології та атмосферу повного спокою.
          </p>
        </header>

        {/* СЕКЦІЯ ПСИХІАТРІЇ */}
        <section className="category-section">
          <div 
            className={`category-trigger ${activeCategory === 'psychiatry' ? 'active' : ''}`}
            onClick={() => toggleCategory('psychiatry')}
          >
            <h3 className="category-title">
              <span className="cat-icon">🩺</span> 
              Психіатрична допомога дорослим
            </h3>
            <span className="chevron-icon">▼</span>
          </div>
          {renderSimpleList(groupedServices.psychiatry, activeCategory === 'psychiatry')}
        </section>

        {/* СЕКЦІЯ ПСИХОЛОГІЇ */}
        <section className="category-section">
          <div 
            className={`category-trigger ${activeCategory === 'psychology' ? 'active' : ''}`}
            onClick={() => toggleCategory('psychology')}
          >
            <h3 className="category-title">
              <span className="cat-icon">🧠</span> 
              Психологічна допомога дорослим
            </h3>
            <span className="chevron-icon">▼</span>
          </div>
          {renderSimpleList(groupedServices.psychology, activeCategory === 'psychology')}
        </section>

        <div className="services-footer-note">
          <p>Не знайшли потрібну інформацію? Зверніться до реєстратури.</p>
        </div>
      </div>

      <style>{`
        .services-page-container {
          --mint: #1abc9c;
          --mint-dark: #16a085;
          --text-dark: #2d5a50;
          --bg-light: #f9fdfc;
          --border-color: #c2ded7;
          
          background-color: var(--bg-light);
          padding: clamp(30px, 6vw, 60px) 15px;
          font-family: 'Montserrat', sans-serif;
          min-height: 100vh;
        }

        .services-content { max-width: 800px; margin: 0 auto; }

        .services-header { text-align: center; margin-bottom: 40px; }
        .services-main-title { 
          font-size: clamp(24px, 5vw, 34px); 
          color: var(--text-dark); 
          font-weight: 700;
          margin-bottom: 12px;
        }
        .services-divider { 
          width: 60px; height: 4px; 
          background: linear-gradient(90deg, var(--mint), var(--mint-dark)); 
          margin: 0 auto; border-radius: 10px; 
        }
        .services-subtitle { 
          color: #5f7d75; font-size: 15px; 
          max-width: 600px; margin: 20px auto 0; line-height: 1.5; 
        }

        /* Стилі тригера (заголовка, що клікається) */
        .category-trigger {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fff;
          padding: 20px 25px;
          border-radius: 15px;
          cursor: pointer;
          border: 2px solid var(--border-color);
          transition: all 0.3s ease;
          margin-top: 15px;
          user-select: none;
        }

        .category-trigger:hover {
          border-color: var(--mint);
          background: #f0fbf9;
        }

        .category-trigger.active {
          border-color: var(--mint);
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          background: #fff;
        }

        .category-title {
          font-size: clamp(16px, 4vw, 19px);
          color: var(--text-dark);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          font-weight: 700;
          line-height: 1.3;
        }

        .chevron-icon {
          font-size: 12px;
          color: var(--mint);
          transition: transform 0.3s ease;
        }

        .active .chevron-icon {
          transform: rotate(180deg);
        }

        /* Список та анімація */
        .modern-service-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
          overflow: hidden;
          transition: max-height 0.4s ease-out, opacity 0.3s;
          background: var(--border-color);
          border: 2px solid var(--border-color);
          border-top: none;
          border-bottom-left-radius: 15px;
          border-bottom-right-radius: 15px;
        }

        .is-closed {
          max-height: 0;
          opacity: 0;
          pointer-events: none;
          border: none;
        }

        .is-open {
          max-height: 2000px; /* Достатньо велике значення для контенту */
          opacity: 1;
        }

        .list-item-simple {
          background: #fff;
          padding: 20px 25px;
          display: flex;
          align-items: flex-start;
          gap: 20px;
        }

        .list-item-number {
          font-size: 20px;
          font-weight: 800;
          color: var(--mint);
          opacity: 0.3;
          min-width: 30px;
        }

        .list-item-title {
          font-size: 17px;
          color: #1e2d2a;
          margin: 0 0 5px;
          font-weight: 600;
        }

        .list-item-description {
          color: #5f7d75;
          line-height: 1.5;
          font-size: 14px;
          margin: 0;
        }

        .services-footer-note {
          margin-top: 40px; padding: 20px; text-align: center;
          background: #fff; border-radius: 15px; 
          border: 1px dashed var(--border-color); 
          color: var(--text-dark);
          font-size: 14px;
        }

        /* Адаптивність для мобільних */
        @media (max-width: 600px) {
          .category-trigger { padding: 15px 18px; }
          .list-item-simple { padding: 15px 18px; gap: 15px; }
          .list-item-number { font-size: 16px; min-width: 25px; }
          .list-item-title { font-size: 15px; }
          .list-item-description { font-size: 13px; }
        }
      `}</style>
    </div>
  );
}